/**
 * @fileoverview Default Modal component used throughout the application
 * @active
 * @see {@link src/components/auth/TwoFAModal.tsx} For usage with 2FA authentication
 * @see {@link src/components/ui/CTAmodal.tsx} For usage with call-to-action modals
 */

import { memo, ReactNode } from "react";

import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  FlexProps,
  ModalProps,
  ModalContentProps,
  ModalOverlayProps,
} from "@chakra-ui/react";

/**
 * Props for the DefaultModal component
 * @interface DefaultModalProps
 */
interface DefaultModalProps {
  /** Modal content */
  children: ReactNode;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  onClose: () => void;
  /** Style overrides for the modal */
  modalStyle?: ModalContentProps;
  /** Style overrides for the overlay */
  overlayStyle?: ModalOverlayProps;
  /** Style overrides for the content wrapper */
  content_style: FlexProps;
  /** Whether to show the close button */
  isCloseBtn?: boolean;
}

/**
 * Default modal component used throughout the application
 * Provides a consistent modal experience with customizable styling
 *
 * @param {DefaultModalProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const DefaultModal = ({
  children,
  isOpen,
  onClose,
  modalStyle = {},
  overlayStyle = {},
  content_style,
  isCloseBtn = true,
}: DefaultModalProps): JSX.Element => {
  return (
    <Modal
      blockScrollOnMount={true}
      isOpen={isOpen}
      onClose={onClose}
      isCentered
    >
      <ModalOverlay bg={"rgba(31, 25, 9, 0.49)"} {...overlayStyle} />
      <ModalContent
        mx={"24px"}
        maxW={"fit-content"}
        borderRadius={"10px"}
        {...modalStyle}
      >
        {isCloseBtn && <ModalCloseButton color={"colors.icon"} />}
        <ModalBody p={"0px"} m={"0px"}>
          <Flex
            flexDir={"column"}
            maxW={"fit-content"}
            p={"24px"}
            {...content_style}
          >
            {children}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(DefaultModal);
